"use client";

import { Card } from "@/components/ui-system";

import type { DashboardWidgetDto } from "@/lib/api/types";

import { alertBindingFingerprintFromWidgetConfig } from "@/modules/alerts/utils/alert-binding-fingerprint";

import { useDashboardRuntime } from "./DashboardRuntimeContext";
import { isFixedTimeRangeKey, parseStandardWidgetConfig } from "./widget-config";
import { GaugeWidget } from "./widgets/GaugeWidget";
import { LineChartWidget } from "./widgets/LineChartWidget";
import { StatWidget } from "./widgets/StatWidget";
import { TableWidget } from "./widgets/TableWidget";

type WidgetRendererProps = {
  widget: DashboardWidgetDto;
};

export function WidgetRenderer({ widget }: WidgetRendererProps) {
  const rt = useDashboardRuntime();
  const parsed = parseStandardWidgetConfig(widget.config ?? {});

  if (!parsed.ok) {
    return (
      <Card className="flex h-full min-h-[10rem] flex-col border-error/35 bg-error/5 p-ds-4 shadow-card" role="alert">
        <p className="text-caption font-semibold text-error">Invalid widget config</p>
        <p className="mt-ds-2 text-caption text-muted">{parsed.error}</p>
      </Card>
    );
  }

  const config = parsed.data;
  const timeRange = isFixedTimeRangeKey(rt?.globalTimeRange?.key) ? rt.globalTimeRange.key : config.time_range;
  const customRange = rt?.globalTimeRange?.key === "custom" ? rt.globalTimeRange : null;
  const fp = alertBindingFingerprintFromWidgetConfig(config);
  const hasActiveAlert = Boolean(fp && rt?.activeAlertFingerprints.has(fp));

  switch (widget.type) {
    case "line_chart":
      return (
        <LineChartWidget widget={widget} config={config} timeRange={timeRange} customRange={customRange} hasActiveAlert={hasActiveAlert} />
      );
    case "gauge":
      return <GaugeWidget widget={widget} config={config} timeRange={timeRange} customRange={customRange} hasActiveAlert={hasActiveAlert} />;
    case "stat":
      return <StatWidget widget={widget} config={config} timeRange={timeRange} customRange={customRange} hasActiveAlert={hasActiveAlert} />;
    case "table":
      return <TableWidget widget={widget} config={config} timeRange={timeRange} customRange={customRange} />;
    default:
      return (
        <Card className="flex h-full min-h-[10rem] flex-col border-border/60 p-ds-4 shadow-card">
          <p className="text-caption text-muted">Unsupported widget type.</p>
        </Card>
      );
  }
}
